import { useEffect, useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import CustomerWorkspace from './CustomerWorkspace';
import CustomerEditor from './CustomerEditor';
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return s; }
};

const statusOf = err => err?.status ?? err?.response?.status;
const legacyId = value => String(value || '').startsWith('legacy:') ? String(value).slice(7) : String(value || '');
const roleOf = roles => roles?.includes('customer') && roles?.includes('supplier') ? 'both' : roles?.includes('supplier') ? 'supplier' : 'customer';
const fromReadModel = item => {
  const preferred = (item.balances || []).find(b => b.currency === 'TRY') || (item.balances || [])[0];
  const actionNames = ['view_ledger', 'view_orders', 'edit', 'delete', 'collect'];
  const actions = Object.fromEntries(actionNames.map(name => [name, item.actions?.[name] === true]));
  return {
    id: item.id, source: item.source, name: item.name || 'İsimsiz cari', contact_role: roleOf(item.roles),
    balance: preferred?.balance ?? null, currency: preferred?.currency || 'TRY', balances: item.balances || [],
    multi_currency: (item.balances || []).length > 1,
    balance_available: item.balance_available === true, last_movement_date: item.last_movement_date,
    actions,
  };
};

const ledgerFromReadModel = item => ({
  id: item.id, date: item.occurred_on, description: ({ opening_balance: 'Açılış bakiyesi', invoice: 'Fatura', collection: 'Tahsilat', payment: 'Ödeme', refund: 'İade', adjustment: 'Düzeltme', reversal: 'Ters kayıt' })[item.kind] || 'Hesap hareketi',
  debit: item.side === 'debit' ? item.amount : null, credit: item.side === 'credit' ? item.amount : null,
  balance: item.balance_after, currency: item.currency, reversed: item.reversed,
});


const EMPTY_FORM = {
  contact_role: "customer",
  customer_type: "individual",
  name: "", short_name: "",
  tax_no: "", tax_office: "",
  email: "", phone: "", fax: "",
  address: "", postal_code: "", district: "", city: "",
  iban: "", opening_balance: "", currency: "TRY",
  category: "", notes: "",
  contact_person: { name: "", email: "", phone: "", notes: "" },
};


export default function CustomersPage() {
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [page, setPage]             = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [summary, setSummary]       = useState({ total_contacts: 0, open_balance_count: 0, total_receivable: 0, total_payable: 0, customer_count: 0, supplier_count: 0 });
  const [balanceFilter, setBalanceFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [sort, setSort] = useState("name_asc");

  // Seçili müşteri (sağ panel)
  const [selected, setSelected]           = useState(null);
  const [orders, setOrders]               = useState([]);
  const [detailTab, setDetailTab]         = useState("info"); // info | acik_hesap | satislar
  const [detailLoading, setDetailLoading] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [detailError, setDetailError] = useState('');
  const listScroll = useRef(0);
  const detailRequest = useRef(0);

  // TAHSİLAT EKLE modal
  const [showTahsilat, setShowTahsilat]   = useState(false);
  const [tahsilatForm, setTahsilatForm]   = useState({ amount: "", payment_method: "nakit", note: "", payment_date: "" });
  const [savingTahsilat, setSavingTahsilat] = useState(false);

  // Form (yeni / düzenle)
  const [showForm, setShowForm]   = useState(false);
  const [editMode, setEditMode]   = useState(false); // false = yeni, true = düzenle
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [syncing, setSyncing]     = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const requestRef = useRef(0);
  const PER_PAGE  = 30;

  // ── Yükle ────────────────────────────────────────────────────────────────
  const loadCustomers = useCallback(async (q = "", p = 1) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    try {
      const rm = new URLSearchParams({ per_page: PER_PAGE, page: p, role: roleFilter || 'all', sort });
      if (q.trim()) rm.set('q', q.trim());
      if (balanceFilter) { rm.set('balance', balanceFilter); rm.set('currency', 'TRY'); }
      if (sort.startsWith('balance_')) rm.set('currency', 'TRY');
      let data;
      let readModel = true;
      try {
        data = await api.get(`/accounting/read-model/parties?${rm}`, { silent_statuses: [404], no_retry: true });
      } catch (err) {
        if (statusOf(err) !== 404) throw err;
        readModel = false;
        const params = new URLSearchParams({ per_page: PER_PAGE, page: p });
        if (q.trim()) params.set("search", q.trim());
        if (balanceFilter) params.set("balance_status", balanceFilter);
        if (roleFilter) params.set("role", roleFilter);
        params.set("sort", sort);
        data = await api.get(`/customers?${params}`);
      }
      if (requestId !== requestRef.current) return;
      const received = (data.items || []).map(item => readModel ? fromReadModel(item) : ({ ...item, source: item.source || 'legacy', actions: item.actions || { view_ledger: true, view_orders: true, edit: true, delete: true, collect: true }, balance_available: item.balance_available !== false }));
      // Sunucu/ara katman eski bir filtrelenmemiş yanıt döndürse dahi rol
      // filtresinden farklı kayıtların ekrana sızmasına izin verme.
      const roleSafeItems = roleFilter
        ? received.filter(c => roleFilter === "both" ? c.contact_role === "both" : [roleFilter, "both"].includes(c.contact_role || "customer"))
        : received;
      setCustomers(roleSafeItems);
      setTotal(roleFilter && roleSafeItems.length !== received.length ? roleSafeItems.length : (data.total || 0));
      setTotalPages(roleFilter && roleSafeItems.length !== received.length ? 1 : (readModel ? Math.max(1, Math.ceil((data.total || 0) / (data.per_page || PER_PAGE))) : (data.total_pages || 1)));
      setSummary(readModel ? { available: false } : { ...(data.summary || {}), available: true });
    } catch (err) {
      if (requestId === requestRef.current) {
        setCustomers([]); setTotal(0); setTotalPages(1); setSummary({ available: false });
      }
      if (requestId === requestRef.current) toast.error(err.message || "Müşteriler yüklenemedi");
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [balanceFilter, roleFilter, sort]);

  // Arama, filtre, sıralama ve sayfalama tek istek hattından geçer. Ayrı
  // effect'ler aynı anda eski/yeni filtrelerle istek atıp sonucu ezemez.
  useEffect(() => {
    const t = setTimeout(() => loadCustomers(search, page), search ? 320 : 0);
    return () => clearTimeout(t);
  }, [search, page, balanceFilter, roleFilter, sort, loadCustomers]);

  // ── Müşteri detay ────────────────────────────────────────────────────────
  const openDetail = async (c) => {
    const request = ++detailRequest.current;
    if (!selected) listScroll.current = window.scrollY;
    window.scrollTo(0, 0);
    setSelected(c);
    setDetailTab(c.actions?.view_ledger === false ? "info" : "ledger");
    setLedger([]);
    setLedgerPage(1);
    setLedgerTotal(0);
    setDetailError('');
    setDetailLoading(true);
    try {
      let ordRes = { items: [] };
      let ledgerRes = { items: [] };
      let detail = c;
      if (c.source === 'obihub') {
        const eid = encodeURIComponent(c.id);
        [detail, ledgerRes] = await Promise.all([
          api.get(`/accounting/read-model/parties/${eid}`),
          c.actions?.view_ledger === true ? api.get(`/accounting/read-model/parties/${eid}/ledger?per_page=100&page=1`) : Promise.resolve({ items: [], total: 0 }),
        ]);
        detail = { ...c, ...fromReadModel(detail) };
        ledgerRes = { ...ledgerRes, items: (ledgerRes.items || []).map(ledgerFromReadModel) };
      } else {
        const eid = encodeURIComponent(legacyId(c.id));
        [ordRes, ledgerRes] = await Promise.all([
          c.actions?.view_orders === false ? Promise.resolve({ items: [] }) : api.get(`/customers/${eid}/orders`),
          c.actions?.view_ledger === false ? Promise.resolve({ items: [] }) : api.get(`/customers/${eid}/ledger`),
        ]);
      }
      if (request !== detailRequest.current) return;
      setLedger(ledgerRes.items || []);
      setLedgerTotal(ledgerRes.total ?? (ledgerRes.items || []).length);
      setSelected(prev => prev?.id === c.id ? {...prev, ...detail, ...(ledgerRes.balance !== undefined ? { balance: ledgerRes.balance } : {})} : prev);
      setOrders(ordRes.items || []);
    } catch (err) {
      if (request === detailRequest.current) setDetailError(err.message || 'Lütfen tekrar deneyin.');
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const loadLedgerPage = async (nextPage) => {
    if (!selected || selected.source !== 'obihub' || nextPage < 1) return;
    const request = ++detailRequest.current;
    setDetailLoading(true); setDetailError('');
    try {
      const eid = encodeURIComponent(selected.id);
      const res = await api.get(`/accounting/read-model/parties/${eid}/ledger?per_page=100&page=${nextPage}`);
      if (request !== detailRequest.current) return;
      setLedger((res.items || []).map(ledgerFromReadModel));
      setLedgerTotal(res.total || 0); setLedgerPage(nextPage);
    } catch (err) {
      if (request === detailRequest.current) setDetailError(err.message || 'Lütfen tekrar deneyin.');
    } finally {
      if (request === detailRequest.current) setDetailLoading(false);
    }
  };

  const handleTahsilatSave = async (e) => {
    e.preventDefault();
    if (!selected || selected.source === 'obihub' || selected.actions?.collect !== true) { toast.error('Bu cari için tahsilat işlemi etkin değil'); return; }
    if (!tahsilatForm.amount || Number(tahsilatForm.amount) <= 0) {
      toast.error("Geçerli bir tutar giriniz"); return;
    }
    setSavingTahsilat(true);
    try {
      if (selected.actions?.collect === false) throw new Error('Bu cari için tahsilat işlemi etkin değil');
      const eid = encodeURIComponent(legacyId(selected.id));
      await api.post(`/customers/${eid}/payments`, {
        amount: Number(tahsilatForm.amount),
        payment_method: tahsilatForm.payment_method,
        note: tahsilatForm.note,
        payment_date: tahsilatForm.payment_date || undefined,
      });
      toast.success(`₺${fmt(tahsilatForm.amount)} tahsilat kaydedildi`);
      setShowTahsilat(false);
      setTahsilatForm({ amount: "", payment_method: "nakit", note: "", payment_date: "" });
      // Ödemeleri yenile
      // Müşteri bakiyesini güncelle (optimistic)
      const paid = Number(tahsilatForm.amount);
      setSelected((prev) => prev ? { ...prev, balance: Math.max(0, (prev.balance || 0) - paid) } : prev);
      setCustomers((prev) => prev.map((c) =>
        c.id === selected.id ? { ...c, balance: Math.max(0, (c.balance || 0) - paid) } : c
      ));
      await openDetail(selected);
      loadCustomers(search, page);
    } catch (err) {
      toast.error(err.message || "Tahsilat kaydedilemedi");
    } finally {
      setSavingTahsilat(false);
    }
  };

  // ── Form helpers ─────────────────────────────────────────────────────────
  const openNew = () => {
    setForm(EMPTY_FORM); setEditMode(false); setShowForm(true);
  };

  const openEdit = (c) => {
    if (c.source === 'obihub' || c.actions?.edit !== true) return;
    setForm({
      customer_type: c.customer_type || "individual",
      contact_role: c.contact_role || "customer",
      name: c.name || "", short_name: c.short_name || "",
      tax_no: c.tax_no || "", tax_office: c.tax_office || "",
      email: c.email || "", phone: c.phone || "", fax: c.fax || "",
      address: c.address || "", postal_code: c.postal_code || "",
      district: c.district || "", city: c.city || "",
      iban: c.iban || "", opening_balance: c.opening_balance || "",
      currency: c.currency || "TRY",
      category: c.category || "", notes: c.notes || "",
      contact_person: c.contact_person || { name: "", email: "", phone: "", notes: "" },
    });
    setEditMode(c.source !== 'orders'); setShowForm(true);
    if (c.source === 'orders') toast.info('Siparişten gelen bilgileri bir cari kartı olarak kaydedebilirsiniz.');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Müşteri adı zorunludur"); return; }
    setSaving(true);
    try {
      if (editMode && selected) {
        const updated = await api.put(`/customers/${encodeURIComponent(legacyId(selected.id))}`, {...form, opening_balance: Number(form.opening_balance || 0)});
        setSelected(updated);
        setCustomers((p) => p.map((c) => c.id === updated.id ? updated : c));
        toast.success("Müşteri güncellendi");
        await openDetail(updated);
      } else {
        const created = await api.post("/customers", {...form, opening_balance: Number(form.opening_balance || 0)});
        setCustomers((p) => [created, ...p]);
        setTotal((t) => t + 1);
        toast.success("Müşteri oluşturuldu");
        await openDetail(created);
      }
      setShowForm(false);
      loadCustomers(search, page);
    } catch (err) {
      toast.error(err.message || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected || selected.source === 'obihub' || selected.actions?.delete !== true) return;
    setDeleting(true);
    try {
      await api.delete(`/customers/${encodeURIComponent(legacyId(selected.id))}`);
      setCustomers((p) => p.filter((c) => c.id !== selected.id));
      setTotal((t) => t - 1);
      setSelected(null);
      setShowDeleteConfirm(false);
      toast.success("Müşteri silindi");
    } catch (err) {
      toast.error(err.message || "Silinemedi");
    } finally {
      setDeleting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post("/customers/sync-from-orders", {});
      toast.success(`${res.created} yeni müşteri içe aktarıldı (${res.processed} sipariş tarandı)`);
      loadCustomers(search, page);
    } catch (err) {
      toast.error(err.message || "Senkronizasyon başarısız");
    } finally {
      setSyncing(false);
    }
  };

  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="cw-page">

      {showForm ? <CustomerEditor {...{form, setForm, editMode, saving}} onSave={handleSave} onClose={() => setShowForm(false)} /> : <CustomerWorkspace {...{customers, summary, total, loading, search, setSearch, roleFilter, setRoleFilter, balanceFilter, setBalanceFilter, sort, setSort, page, setPage, totalPages, selected, openDetail, openNew, openEdit, handleSync, syncing, detailTab, setDetailTab, detailLoading, ledger, ledgerPage, ledgerTotal, loadLedgerPage, orders, detailError}}
        closeDetail={() => { setSelected(null); requestAnimationFrame(() => window.scrollTo(0, listScroll.current)); }}
        collect={() => { if (selected.actions?.collect === false) return; const amount = Number(selected.balance); setShowTahsilat(true); setTahsilatForm({ amount: Number.isFinite(amount) && amount > 0 ? amount.toFixed(2) : "", payment_method: "nakit", note: "", payment_date: "" }); }} />}


      {/* Slide-over: TAHSİLAT EKLE */}
      {showTahsilat && createPortal(
        <div className="cw-slideover" onClick={e => { if (e.target === e.currentTarget) setShowTahsilat(false); }}>
          <div className="cw-slideover-backdrop" onClick={() => setShowTahsilat(false)} />
          <div className="cw cw-slideover-panel">
            <div className="cw-slideover-head">
              <div style={{display:'flex',alignItems:'center',gap:'12px',minWidth:0,flex:1}}>
                <span style={{width:'36px',height:'36px',borderRadius:'8px',background:'#e2f4f8',color:'#1487a3',display:'grid',placeItems:'center',flexShrink:0}}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </span>
                <div style={{minWidth:0}}>
                  <span className="cw-eyebrow">TAHSİLAT EKLE</span>
                  <div style={{fontSize:'15px',fontWeight:700,fontFamily:'Manrope,Inter,sans-serif',marginTop:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{selected?.name}</div>
                </div>
              </div>
              <button onClick={() => setShowTahsilat(false)} aria-label="Kapat" style={{width:'32px',height:'32px',minHeight:'32px',padding:0}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleTahsilatSave} style={{display:'flex',flexDirection:'column',flex:1,minHeight:0}}>
              <div className="cw-slideover-body">
                <label className="cw-field">
                  <span>Tutar *</span>
                  <div className="cw-amount-wrap">
                    <span className="cw-amount-prefix">₺</span>
                    <input className="cw-amount-input" type="number" value={tahsilatForm.amount}
                      onChange={e => setTahsilatForm({...tahsilatForm, amount: e.target.value})}
                      placeholder="0,00" step="0.01" min="0.01" required autoFocus />
                  </div>
                  {selected?.balance > 0 && (
                    <small>Açık bakiye:&nbsp;
                      <button type="button" className="cw-link-btn"
                        onClick={() => setTahsilatForm(f => ({...f, amount: String(selected.balance.toFixed(2))}))}>
                        ₺{fmt(selected.balance)} tahsil et
                      </button>
                    </small>
                  )}
                </label>
                <div>
                  <p className="cw-choice-label">Ödeme Yöntemi</p>
                  <div className="cw-choices cw-choices-2x2">
                    {[["nakit","Nakit"],["kart","Kart"],["havale","Havale / EFT"],["çek","Çek"]].map(([val,lbl]) => (
                      <button key={val} type="button"
                        onClick={() => setTahsilatForm(f => ({...f, payment_method: val}))}
                        className={tahsilatForm.payment_method === val ? "active" : ""}>
                        <span><strong>{lbl}</strong></span>
                      </button>
                    ))}
                  </div>
                </div>
                <label className="cw-field">
                  <span>Tarih</span>
                  <input type="date" value={tahsilatForm.payment_date}
                    onChange={e => setTahsilatForm(f => ({...f, payment_date: e.target.value}))} />
                </label>
                <label className="cw-field">
                  <span>Not</span>
                  <input type="text" value={tahsilatForm.note}
                    onChange={e => setTahsilatForm(f => ({...f, note: e.target.value}))}
                    placeholder="Opsiyonel not…" />
                </label>
              </div>
              <div className="cw-slideover-foot">
                <button type="button" onClick={() => setShowTahsilat(false)} style={{flex:1}}>Vazgeç</button>
                <button type="submit" disabled={savingTahsilat} className="cw-primary" style={{flex:2}}>
                  {savingTahsilat ? "Kaydediliyor…" : "Tahsilat Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      , document.body)}

      {/* Modal: Silme onayı */}
      {showDeleteConfirm && createPortal(
        <div className="cw-modal-backdrop" onClick={e => { if (e.target === e.currentTarget) setShowDeleteConfirm(false); }}>
          <div className="cw cw-modal">
            <div className="cw-modal-head">
              <div className="cw-modal-icon danger">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6M10 11v6M14 11v6M9 6V4h6v2"/></svg>
              </div>
              <h3 style={{fontFamily:'Manrope,Inter,sans-serif',fontSize:'17px',fontWeight:750,margin:'0 0 8px',letterSpacing:'-.025em'}}>Müşteriyi Sil</h3>
              <p style={{fontSize:'13px',color:'var(--cw-muted)',margin:0,lineHeight:1.65}}><strong>{selected?.name}</strong> adlı müşteri ve tüm hesap hareketleri kalıcı olarak silinecek.</p>
            </div>
            <div className="cw-modal-foot">
              <button onClick={() => setShowDeleteConfirm(false)} style={{flex:1}}>Vazgeç</button>
              <button onClick={handleDelete} disabled={deleting} className="cw-danger" style={{flex:1}}>
                {deleting ? "Siliniyor…" : "Evet, Sil"}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
